import CustomButton from "@components/common/Button";
import LoadingSpinner from "@components/common/LoadingSpinner";
import NotFound from "@components/common/NotFound";
import { notify } from "@components/common/Notification";
import EmailConfigurationService from "@/services/APIs/EmailConfigurationService";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Checkbox, Form, Input } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const AddEmailConfigurationPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);

  // Use React Query for getting email config by ID
  const {
    data: emailConfig,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["emailConfigurations", id],
    queryFn: () =>
      EmailConfigurationService.getEmailConfigurationById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && emailConfig) {
      form.setFieldsValue(emailConfig);
    }
  }, [isEditMode, emailConfig, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    if (isEditMode) {
      await EmailConfigurationService.updateEmailConfiguration(id!, values);
    } else {
      const response =
        await EmailConfigurationService.createEmailConfiguration(values);
      if (response) {
        notify.success(
          "Email Configuration",
          "Email configuration has been saved."
        );
        navigate(navigationRoutes.EMAIL_CONFIGURATION);
      }
    }
    setLoading(false);
  };

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && isError) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            isEditMode ? "Edit Email Configuration" : "Add Email Configuration"
          }
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
              title={
                isEditMode
                  ? "Save Email Configuration Changes"
                  : "Save New Email Configuration"
              }
            >
              {loading ? "Saving..." : isEditMode ? "Save Changes" : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="smtp_host"
            label="SMTP Host"
            rules={[{ required: true, message: "Please enter SMTP host" }]}
          >
            <Input placeholder="Enter SMTP host" />
          </Form.Item>
          <Form.Item
            name="smtp_port"
            label="SMTP Port"
            rules={[{ required: true, message: "Please enter SMTP port" }]}
          >
            <Input type="number" placeholder="Enter SMTP port" />
          </Form.Item>
          <Form.Item
            name="smtp_username"
            label="SMTP Username"
            rules={[{ required: true, message: "Please enter SMTP username" }]}
          >
            <Input placeholder="Enter SMTP username" />
          </Form.Item>
          <Form.Item
            name="smtp_password"
            label="SMTP Password"
            rules={[{ required: true, message: "Please enter SMTP password" }]}
          >
            <Input.Password placeholder="Enter SMTP password" />
          </Form.Item>
          <Form.Item
            name="from_email"
            label="From Email"
            rules={[{ required: true, message: "Please enter from email" }]}
          >
            <Input placeholder="Enter from email" />
          </Form.Item>
          <Form.Item
            name="display_name"
            label="Display Name"
            rules={[{ required: true, message: "Please enter display name" }]}
          >
            <Input placeholder="Enter display name" />
          </Form.Item>
          <Form.Item
            name="reply_to"
            label="Reply To"
            rules={[{ required: true, message: "Please enter reply to email" }]}
          >
            <Input placeholder="Enter reply to email" />
          </Form.Item>
          <div className="col-span-2">
            <Form.Item name="use_tls" valuePropName="checked">
              <Checkbox>Use TLS</Checkbox>
            </Form.Item>
            <Form.Item name="use_ssl" valuePropName="checked">
              <Checkbox>Use SSL</Checkbox>
            </Form.Item>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default AddEmailConfigurationPage;
